import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumb } from '../components/Breadcrumb';
import { Button, Card, DashboardLayout } from '../components';
import { adminApi, RefundPolicySettings, SellerFeeFundingSource } from '../services/api';

const defaultSettings: RefundPolicySettings = {
  conflictStep1SellerFeeTreatment: 'retain',
  conflictStep1SellerFeeFundingSource: 'etappio_balance',
  withdrawalRightSellerFeeFundingSource: 'etappio_balance',
  conflictLevel2SellerFeeTreatment: 'retain',
  conflictLevel2SellerFeeFundingSource: 'etappio_balance'
};

const fundingOptionKeys: Array<{ value: SellerFeeFundingSource; labelKey: string }> = [
  { value: 'etappio_balance', labelKey: 'adminRefundPolicy.funding.etappioBalance' },
  { value: 'seller_balance', labelKey: 'adminRefundPolicy.funding.sellerBalance' }
];

export function AdminRefundPolicySettings() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<RefundPolicySettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'danger';
    text: string;
  } | null>(null);

  useEffect(() => {
    adminApi
      .getRefundPolicySettings()
      .then((value) => setSettings({ ...defaultSettings, ...value }))
      .catch(() =>
        setMessage({
          type: 'danger',
          text: t('adminRefundPolicy.errors.load')
        })
      )
      .finally(() => setLoading(false));
  }, [t]);

  const update = <K extends keyof RefundPolicySettings>(key: K, value: RefundPolicySettings[K]) => {
    setSettings((current) => ({ ...current, [key]: value }));
    setMessage(null);
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const saved = await adminApi.updateRefundPolicySettings(settings);
      setSettings({ ...defaultSettings, ...saved });
      setMessage({
        type: 'success',
        text: t('adminRefundPolicy.saved')
      });
    } catch {
      setMessage({
        type: 'danger',
        text: t('adminRefundPolicy.errors.save')
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <Breadcrumb
        items={[
          { label: t('adminRefundPolicy.breadcrumb.transactions'), path: '/admin/transactions-menu' },
          { label: t('adminRefundPolicy.breadcrumb.current') }
        ]}
      />

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div>
          <h4 className="mb-1">{t('adminRefundPolicy.title')}</h4>
          <p className="text-muted mb-0">{t('adminRefundPolicy.subtitle')}</p>
        </div>
        <Button variant="primary" onClick={save} disabled={loading || saving}>
          {saving ? t('adminRefundPolicy.saving') : t('adminRefundPolicy.save')}
        </Button>
      </div>

      {message && <div className={`alert alert-${message.type}`}>{message.text}</div>}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : (
        <>
          <div className="row g-3">
            <div className="col-xl-4">
              <Card className="h-100">
                <h6 className="mb-2">{t('adminRefundPolicy.withdrawal.title')}</h6>
                <p className="text-muted small">{t('adminRefundPolicy.withdrawal.desc')}</p>
                <label className="form-label fw-semibold" htmlFor="withdrawal-funding-source">
                  {t('adminRefundPolicy.withdrawal.fundingLabel')}
                </label>
                <select
                  id="withdrawal-funding-source"
                  className="form-select"
                  value={settings.withdrawalRightSellerFeeFundingSource}
                  onChange={(event) =>
                    update('withdrawalRightSellerFeeFundingSource', event.target.value as SellerFeeFundingSource)
                  }
                >
                  {fundingOptionKeys.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </option>
                  ))}
                </select>
              </Card>
            </div>

            <div className="col-xl-4">
              <Card className="h-100">
                <h6 className="mb-2">{t('adminRefundPolicy.step1.title')}</h6>
                <p className="text-muted small">{t('adminRefundPolicy.step1.desc')}</p>
                <label className="form-label fw-semibold" htmlFor="step-1-seller-fee-treatment">
                  {t('adminRefundPolicy.sellerFee')}
                </label>
                <select
                  id="step-1-seller-fee-treatment"
                  className="form-select mb-3"
                  value={settings.conflictStep1SellerFeeTreatment}
                  onChange={(event) =>
                    update('conflictStep1SellerFeeTreatment', event.target.value as 'retain' | 'refund')
                  }
                >
                  <option value="retain">{t('adminRefundPolicy.treatment.retain')}</option>
                  <option value="refund">{t('adminRefundPolicy.treatment.refund')}</option>
                </select>

                <label className="form-label fw-semibold" htmlFor="step-1-funding-source">
                  {t('adminRefundPolicy.fundingOnRefundLabel')}
                </label>
                <select
                  id="step-1-funding-source"
                  className="form-select"
                  value={settings.conflictStep1SellerFeeFundingSource}
                  onChange={(event) =>
                    update('conflictStep1SellerFeeFundingSource', event.target.value as SellerFeeFundingSource)
                  }
                >
                  {fundingOptionKeys.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </option>
                  ))}
                </select>
                <div className="form-text">{t('adminRefundPolicy.step1.note')}</div>
              </Card>
            </div>

            <div className="col-xl-4">
              <Card className="h-100">
                <h6 className="mb-2">{t('adminRefundPolicy.level2.title')}</h6>
                <p className="text-muted small">{t('adminRefundPolicy.level2.desc')}</p>
                <label className="form-label fw-semibold" htmlFor="level-2-seller-fee-treatment">
                  {t('adminRefundPolicy.sellerFee')}
                </label>
                <select
                  id="level-2-seller-fee-treatment"
                  className="form-select mb-3"
                  value={settings.conflictLevel2SellerFeeTreatment}
                  onChange={(event) =>
                    update('conflictLevel2SellerFeeTreatment', event.target.value as 'retain' | 'refund')
                  }
                >
                  <option value="retain">{t('adminRefundPolicy.treatment.retain')}</option>
                  <option value="refund">{t('adminRefundPolicy.treatment.refund')}</option>
                </select>

                <label className="form-label fw-semibold" htmlFor="level-2-funding-source">
                  {t('adminRefundPolicy.fundingOnRefundLabel')}
                </label>
                <select
                  id="level-2-funding-source"
                  className="form-select"
                  value={settings.conflictLevel2SellerFeeFundingSource}
                  onChange={(event) =>
                    update('conflictLevel2SellerFeeFundingSource', event.target.value as SellerFeeFundingSource)
                  }
                >
                  {fundingOptionKeys.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </option>
                  ))}
                </select>
              </Card>
            </div>
          </div>

          <div className="alert alert-light border mt-3 mb-0 small">{t('adminRefundPolicy.footnote')}</div>
        </>
      )}
    </DashboardLayout>
  );
}
